import {
  createContext,
  useContext,
  useCallback,
  useRef,
  useReducer,
  useEffect,
  memo,
  ReactNode,
  JSX,
} from 'react';
import axios from 'src/utils/axios';
import useAuth from 'src/hooks/useAuth';
import useBroadcastChannel from 'src/hooks/useBroadcastChannel';
import {
  POLLING_INTERVALS,
  WATCH_LIST,
  API_ENDPOINTS,
  ERROR_MESSAGES,
  API_QUERY_PARAMS,
} from 'src/constants/realTime';
import { useEventTracking } from 'src/hooks/useEventTracking';
import { WatchListState, WatchListAction } from 'src/types/WatchList';
import { WatchedCertification } from 'src/types/WatchList';

interface WatchListContextType {
  // State
  watchedCerts: WatchedCertification[];
  watchedCertsCount: number;
  watchedCertsLimit: number;
  loading: boolean;
  updating: boolean;
  error: string | null;
  selectedWatchedIds: number[];

  // Actions
  fetchWatchedCerts: (showLoading?: boolean) => Promise<void>;
  handleRemoveWatch: (certIds: number[]) => Promise<void>;
  handleBatchRemove: (certIds: number[]) => Promise<void>;
  handleAddToWatchList: (certIds: number[]) => Promise<void>;

  // Selection Actions
  handleToggleSelection: (certId: number) => void;
  handleSelectAll: () => void;
  handleClearSelection: () => void;
}

// Watch List Action types
const WATCH_LIST_ACTIONS = {
  SET_WATCHED_CERTS: 'SET_WATCHED_CERTS',
  SET_LOADING: 'SET_LOADING',
  SET_UPDATING: 'SET_UPDATING',
  SET_COUNT_AND_LIMIT: 'SET_COUNT_AND_LIMIT',
  SET_ERROR: 'SET_ERROR',
  ADD_TO_WATCHED: 'ADD_TO_WATCHED',
  REMOVE_FROM_WATCHED: 'REMOVE_FROM_WATCHED',
  BATCH_REMOVE_FROM_WATCHED: 'BATCH_REMOVE_FROM_WATCHED',
  RESET: 'RESET',
  TOGGLE_SELECTION: 'TOGGLE_SELECTION',
  SELECT_ALL: 'SELECT_ALL',
  CLEAR_SELECTION: 'CLEAR_SELECTION',
} as const;

// Initial watch list state
const initialWatchListState: WatchListState = {
  watchedCerts: [],
  watchedCertsCount: 0,
  watchedCertsLimit: WATCH_LIST.FALLBACK_LIMIT, // Fallback value, will be updated from backend
  loading: false,
  updating: false,
  error: null,
  selectedWatchedIds: [],
};

// Watch List reducer
const watchListReducer = (
  state: WatchListState,
  action: WatchListAction
): WatchListState => {
  switch (action.type) {
    case WATCH_LIST_ACTIONS.SET_WATCHED_CERTS:
      return {
        ...state,
        watchedCerts: action.payload,
        loading: false,
        error: null,
      };
    case WATCH_LIST_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
        error: action.payload ? null : state.error,
      };
    case WATCH_LIST_ACTIONS.SET_UPDATING:
      return {
        ...state,
        updating: action.payload,
      };
    case WATCH_LIST_ACTIONS.SET_COUNT_AND_LIMIT:
      return {
        ...state,
        watchedCertsCount: action.payload.count,
        watchedCertsLimit: action.payload.limit,
      };
    case WATCH_LIST_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false,
        updating: false,
      };
    case WATCH_LIST_ACTIONS.ADD_TO_WATCHED:
      return {
        ...state,
        watchedCerts: [...state.watchedCerts, ...action.payload],
        updating: false,
      };
    case WATCH_LIST_ACTIONS.REMOVE_FROM_WATCHED:
      return {
        ...state,
        watchedCerts: state.watchedCerts.filter(
          (cert) => !action.payload.includes(cert.id)
        ),
        updating: false,
      };
    case WATCH_LIST_ACTIONS.BATCH_REMOVE_FROM_WATCHED:
      return {
        ...state,
        watchedCerts: state.watchedCerts.filter(
          (cert) => !action.payload.includes(cert.id)
        ),
        updating: false,
      };
    case WATCH_LIST_ACTIONS.RESET:
      return initialWatchListState;
    case WATCH_LIST_ACTIONS.TOGGLE_SELECTION:
      return {
        ...state,
        selectedWatchedIds: state.selectedWatchedIds.includes(action.payload)
          ? state.selectedWatchedIds.filter((id) => id !== action.payload)
          : [...state.selectedWatchedIds, action.payload],
      };
    case WATCH_LIST_ACTIONS.SELECT_ALL:
      return {
        ...state,
        selectedWatchedIds: state.watchedCerts.map((cert) => cert.id),
      };
    case WATCH_LIST_ACTIONS.CLEAR_SELECTION:
      return {
        ...state,
        selectedWatchedIds: [],
      };
    default:
      return state;
  }
};

// Create context
const WatchListContext = createContext<WatchListContextType | undefined>(
  undefined
);

// Hook to use the context
export const useWatchList = (): WatchListContextType => {
  const context = useContext(WatchListContext);
  if (!context) {
    throw new Error('useWatchList must be used within a WatchListProvider');
  }
  return context;
};

// Provider component
export const WatchListProvider = memo(
  ({ children }: { children: ReactNode }): JSX.Element => {
    const { user } = useAuth();
    const { trackError } = useEventTracking();

    // User and permissions
    const userId = user?.id;
    const canUseWatchList = user?.canUseRealTimeNotificationsWatched;

    // Watch list state
    const [watchListState, dispatch] = useReducer(
      watchListReducer,
      initialWatchListState
    );
    const {
      watchedCerts,
      watchedCertsCount,
      watchedCertsLimit,
      loading,
      updating,
      error,
      selectedWatchedIds,
    } = watchListState;

    // Shared refs
    const watchListUpdatingRef = useRef<boolean>(false);

    // Cross-tab communication
    const {
      message: broadcastMessage,
      isReady: broadcastReady,
      sendMessage: sendBroadcast,
    } = useBroadcastChannel('real-time-notifications');

    // Fetch watched certifications
    const fetchWatchedCerts = useCallback(
      async (showLoading = true): Promise<void> => {
        if (!canUseWatchList) {
          return;
        }

        if (watchListUpdatingRef.current) {
          return;
        }

        watchListUpdatingRef.current = true;

        const startTime = Date.now();
        const isPolling = !showLoading;

        if (showLoading) {
          dispatch({ type: WATCH_LIST_ACTIONS.SET_LOADING, payload: true });
        }
        dispatch({ type: WATCH_LIST_ACTIONS.SET_ERROR, payload: null });

        try {
          const response = await axios.get(
            `${API_ENDPOINTS.WATCHED_CERTIFICATIONS}?expand=${API_QUERY_PARAMS.WATCHED_CERTS_EXPAND}&fields=${API_QUERY_PARAMS.WATCHED_CERTS_FIELDS}`
          );

          const results = response.data.results || response.data || [];

          dispatch({
            type: WATCH_LIST_ACTIONS.SET_WATCHED_CERTS,
            payload: results,
          });

          // Calculate count from results and use constant limit to avoid extra API call
          dispatch({
            type: WATCH_LIST_ACTIONS.SET_COUNT_AND_LIMIT,
            payload: {
              count: results.length,
              limit: WATCH_LIST.FALLBACK_LIMIT,
            },
          });

          // Broadcast updates to other tabs
          if (isPolling && broadcastReady) {
            try {
              sendBroadcast({
                type: 'watch_list_updated',
                data: {
                  watchedCerts: results,
                  count: results.length,
                  limit: WATCH_LIST.FALLBACK_LIMIT,
                },
              });
            } catch {
              // Don't fail the entire request if broadcasting fails
            }
          }
        } catch (error) {
          const responseTime = Date.now() - startTime;

          // Track watch list fetch error
          trackError(error, {
            context: 'watch_list_fetch',
            action: isPolling ? 'polling_error' : 'initial_fetch_error',
            response_time: responseTime,
            polling_interval: POLLING_INTERVALS.REAL_TIME_NOTIFICATIONS,
          });

          dispatch({
            type: WATCH_LIST_ACTIONS.SET_ERROR,
            payload: ERROR_MESSAGES.FAILED_TO_LOAD_WATCHED_CERTS,
          });
        } finally {
          if (showLoading) {
            dispatch({ type: WATCH_LIST_ACTIONS.SET_LOADING, payload: false });
          }
          watchListUpdatingRef.current = false;
        }
      },
      [canUseWatchList, trackError, sendBroadcast, broadcastReady]
    );

    // Watch list actions
    const handleRemoveWatch = useCallback(
      async (certIds: number[]): Promise<void> => {
        if (!userId) {
          return;
        }

        if (watchListUpdatingRef.current) {
          return;
        }

        watchListUpdatingRef.current = true;
        dispatch({ type: WATCH_LIST_ACTIONS.SET_UPDATING, payload: true });
        const originalWatchedCerts = [...watchedCerts];
        const originalCount = watchListState.watchedCertsCount;

        try {
          // Optimistic update
          dispatch({
            type: WATCH_LIST_ACTIONS.REMOVE_FROM_WATCHED,
            payload: certIds,
          });
          dispatch({
            type: WATCH_LIST_ACTIONS.SET_COUNT_AND_LIMIT,
            payload: {
              count: Math.max(0, originalCount - certIds.length),
              limit: watchedCertsLimit,
            },
          });

          // Make API call
          if (certIds.length === 1) {
            await axios.post('user_profile/watched_certifications/remove/', {
              certification_id: certIds[0],
            });
          } else {
            await Promise.all(
              certIds.map((certId) =>
                axios.post('user_profile/watched_certifications/remove/', {
                  certification_id: certId,
                })
              )
            );
          }
        } catch {
          // Revert optimistic update
          dispatch({
            type: WATCH_LIST_ACTIONS.SET_WATCHED_CERTS,
            payload: originalWatchedCerts,
          });
          dispatch({
            type: WATCH_LIST_ACTIONS.SET_COUNT_AND_LIMIT,
            payload: {
              count: originalWatchedCerts.length,
              limit: watchedCertsLimit,
            },
          });
        } finally {
          watchListUpdatingRef.current = false;
        }
      },
      [
        userId,
        watchedCerts,
        watchedCertsLimit,
        watchListState.watchedCertsCount,
      ]
    );

    const handleBatchRemove = useCallback(
      async (certIds: number[]): Promise<void> => {
        if (!userId) {
          return;
        }

        if (watchListUpdatingRef.current) {
          return;
        }

        watchListUpdatingRef.current = true;
        dispatch({ type: WATCH_LIST_ACTIONS.SET_UPDATING, payload: true });
        const originalWatchedCerts = [...watchedCerts];

        try {
          // Optimistic update
          dispatch({
            type: WATCH_LIST_ACTIONS.BATCH_REMOVE_FROM_WATCHED,
            payload: certIds,
          });

          // Make API call
          await Promise.all(
            certIds.map((certId) =>
              axios.post('user_profile/watched_certifications/remove/', {
                certification_id: certId,
              })
            )
          );
        } catch {
          // Revert optimistic update
          dispatch({
            type: WATCH_LIST_ACTIONS.SET_WATCHED_CERTS,
            payload: originalWatchedCerts,
          });
        } finally {
          watchListUpdatingRef.current = false;
        }
      },
      [userId, watchedCerts]
    );

    const handleAddToWatchList = useCallback(
      async (certIds: number[]): Promise<void> => {
        if (!userId) {
          return;
        }

        if (watchListUpdatingRef.current) {
          return;
        }

        watchListUpdatingRef.current = true;
        dispatch({ type: WATCH_LIST_ACTIONS.SET_UPDATING, payload: true });
        const originalWatchedCerts = [...watchedCerts];

        try {
          // Check limit
          const currentlyWatchedIds = watchedCerts.map((cert) => cert.id);
          const newCertIds = certIds.filter(
            (id) => !currentlyWatchedIds.includes(id)
          );
          const newTotalCount = currentlyWatchedIds.length + newCertIds.length;

          if (newTotalCount > watchedCertsLimit) {
            throw new Error(
              `You've reached the maximum of ${watchedCertsLimit} watched certifications.`
            );
          }

          // Optimistic update - immediately add to watched list
          if (newCertIds.length > 0) {
            // Create basic certification objects for optimistic update
            const optimisticCerts = newCertIds.map((id) => ({
              id,
              display_name: `Certification ${id}`, // Placeholder name
              effective_date: new Date().toISOString().split('T')[0], // Today's date as placeholder
              deadline: new Date().toISOString().split('T')[0], // Today's date as placeholder
              household: {
                id: 0,
                name: 'Loading...',
              },
              property: {
                id: 0,
                name: 'Loading...',
                slug: 'loading',
              },
              category: {
                id: 0,
                name: 'Loading...',
              },
              certification_config: {
                id: 0,
                name: 'Loading...',
              },
              compliance_user: {
                id: 0,
                first_name: 'Loading...',
                last_name: '',
              },
            }));

            // Optimistic update - add to watched list
            dispatch({
              type: WATCH_LIST_ACTIONS.ADD_TO_WATCHED,
              payload: optimisticCerts,
            });

            // Optimistic update - update count
            dispatch({
              type: WATCH_LIST_ACTIONS.SET_COUNT_AND_LIMIT,
              payload: {
                count: newTotalCount,
                limit: watchedCertsLimit,
              },
            });
          }

          // Make API call
          await axios.post('user_profile/watched_certifications/add/', {
            certification_ids: certIds,
          });
        } catch {
          // Revert optimistic update
          dispatch({
            type: WATCH_LIST_ACTIONS.SET_WATCHED_CERTS,
            payload: originalWatchedCerts,
          });

          // Revert count to original value
          dispatch({
            type: WATCH_LIST_ACTIONS.SET_COUNT_AND_LIMIT,
            payload: {
              count: originalWatchedCerts.length,
              limit: watchedCertsLimit,
            },
          });
        } finally {
          watchListUpdatingRef.current = false;
        }
      },
      [userId, watchedCerts, watchedCertsLimit]
    );

    // Selection actions
    const handleToggleSelection = useCallback((certId: number) => {
      dispatch({ type: WATCH_LIST_ACTIONS.TOGGLE_SELECTION, payload: certId });
    }, []);

    const handleSelectAll = useCallback(() => {
      dispatch({ type: WATCH_LIST_ACTIONS.SELECT_ALL });
    }, []);

    const handleClearSelection = useCallback(() => {
      dispatch({ type: WATCH_LIST_ACTIONS.CLEAR_SELECTION });
    }, []);

    // Handle cross-tab messages
    useEffect(() => {
      if (!broadcastMessage) {
        return;
      }

      const { type, data } = broadcastMessage;

      if (type === 'watch_list_updated') {
        dispatch({
          type: WATCH_LIST_ACTIONS.SET_WATCHED_CERTS,
          payload: data.watchedCerts,
        });
        dispatch({
          type: WATCH_LIST_ACTIONS.SET_COUNT_AND_LIMIT,
          payload: { count: data.count, limit: data.limit },
        });
      }
    }, [broadcastMessage]);

    const value: WatchListContextType = {
      // State
      watchedCerts,
      watchedCertsCount,
      watchedCertsLimit,
      loading,
      updating,
      error,
      selectedWatchedIds,

      // Actions
      fetchWatchedCerts,
      handleRemoveWatch,
      handleBatchRemove,
      handleAddToWatchList,

      // Selection Actions
      handleToggleSelection,
      handleSelectAll,
      handleClearSelection,
    };

    return (
      <WatchListContext.Provider value={value}>
        {children}
      </WatchListContext.Provider>
    );
  }
);

WatchListProvider.displayName = 'WatchListProvider';
